/**
 * Hero Scroll CTA
 * Scroll-driven CTA movement, native JS only.
 * Supports multiple instances on the same page (each fully scoped in a closure).
 */
(function () {
  'use strict';

  var SELECTOR = '[data-hero-scroll-cta]';
  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function lerp(start, end, t) {
    return start + (end - start) * t;
  }

  var EASINGS = {
    linear: function (t) {
      return t;
    },
    ease_out: function (t) {
      return 1 - Math.pow(1 - t, 2);
    },
    ease_in: function (t) {
      return t * t;
    },
    ease_in_out: function (t) {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }
  };

  function getEasing(name) {
    return EASINGS[name] || EASINGS.ease_out;
  }

  function initInstance(root) {
    var pin = root.querySelector('[data-hero-pin]');
    if (!pin) return;

    initTransparentHeader(root, pin);

    // Reduced motion: render a calm static state, no scroll listener at all.
    if (reducedMotionQuery.matches) {
      root.setAttribute('data-reduced-motion', '');
      return;
    }

    var btnStartDesktop = parseFloat(root.dataset.btnStartDesktop) || 55;
    var btnEndDesktop = parseFloat(root.dataset.btnEndDesktop) || 85;
    var btnStartMobile = parseFloat(root.dataset.btnStartMobile) || 58;
    var btnEndMobile = parseFloat(root.dataset.btnEndMobile) || 88;
    var fadeProgress = parseFloat(root.dataset.fadeProgress);
    if (!fadeProgress || fadeProgress <= 0) fadeProgress = 0.35;

    var buttonDelay = parseFloat(root.dataset.buttonDelay);
    if (isNaN(buttonDelay) || buttonDelay < 0) buttonDelay = 0;
    if (buttonDelay >= 1) buttonDelay = 0.9;

    var buttonEase = getEasing(root.dataset.buttonEasing);

    var revealFill = parseFloat(root.dataset.revealFill);
    if (isNaN(revealFill) || revealFill < 0) revealFill = 0.25;
    if (revealFill >= 1) revealFill = 0.9;

    var ticking = false;
    var isInView = false;
    var mobileBreakpoint = 750;
    var rafId = null;

    // Smoothed (displayed) progress chases the real scroll-computed progress
    // every frame. The chase factor is high enough that it's still tightly
    // coupled to the scroll (no perceptible lag/rubber-banding), but it
    // absorbs the small stutters mouse-wheel/trackpad scrolling produces —
    // that's what makes the motion read as smooth/premium instead of jittery.
    var SMOOTHING = 0.22;
    var displayedProgress = null;

    function isMobile() {
      return window.innerWidth < mobileBreakpoint;
    }

    function computeTargetProgress() {
      var rect = root.getBoundingClientRect();
      var pinHeight = pin.getBoundingClientRect().height;
      var vh = window.innerHeight;

      // Unified progress (0 → 1) that starts the moment the hero has scrolled
      // in to cover `revealFill` of the screen, and reaches 1 exactly when the
      // pin actually releases. `pinHeight` is measured directly from the DOM
      // every frame (works whether the pin is a fixed viewport height OR a
      // fixed aspect-ratio), so this stays correct at any screen size and
      // never drifts into a gap or an early/late release.
      var startPoint = (1 - revealFill) * vh;
      var endPoint = pinHeight - rect.height;
      var totalRange = startPoint - endPoint;

      var progress = 0;
      if (totalRange > 0) {
        progress = (startPoint - rect.top) / totalRange;
      }
      return clamp(progress, 0, 1);
    }

    function applyProgress(progress) {
      // "Active" once progress has actually started — used to fade the
      // button/caption in instead of having them appear pre-positioned.
      root.classList.toggle('is-pinned', progress > 0.001);

      var mobile = isMobile();
      var btnStart = mobile ? btnStartMobile : btnStartDesktop;
      var btnEnd = mobile ? btnEndMobile : btnEndDesktop;

      // Button stays put for the first `buttonDelay` portion of the scroll,
      // then travels using the chosen easing curve — this is what lets the
      // button be slowed down / held back relative to the rest of the motion.
      var btnRawT = clamp((progress - buttonDelay) / (1 - buttonDelay), 0, 1);
      var btnT = buttonEase(btnRawT);
      var btnTop = lerp(btnStart, btnEnd, btnT);

      var fadeT = clamp(progress / fadeProgress, 0, 1);
      var headingOpacity = 1 - fadeT;
      var headingY = lerp(0, -32, fadeT);

      pin.style.setProperty('--btn-top', btnTop.toFixed(2));
      pin.style.setProperty('--heading-opacity', headingOpacity.toFixed(3));
      pin.style.setProperty('--heading-y', headingY.toFixed(2) + 'px');
    }

    function loop() {
      var target = computeTargetProgress();

      if (displayedProgress === null) {
        displayedProgress = target;
      } else {
        displayedProgress += (target - displayedProgress) * SMOOTHING;
        if (Math.abs(target - displayedProgress) < 0.0008) {
          displayedProgress = target;
        }
      }

      applyProgress(displayedProgress);

      // Keep chasing while there's still a visible gap between where we are
      // and where the scroll position actually is, or while still in view
      // (covers the very common case of the user stopping mid-scroll).
      if (isInView) {
        rafId = window.requestAnimationFrame(loop);
      } else {
        rafId = null;
      }
    }

    function startLoop() {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(loop);
      }
    }

    function onResize() {
      startLoop();
    }

    // Only run the smoothing loop while the section is actually near the
    // viewport, to avoid unnecessary work on long pages.
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          isInView = entry.isIntersecting;
          if (isInView) startLoop();
        });
      },
      { rootMargin: '20% 0px 20% 0px' }
    );
    observer.observe(root);

    window.addEventListener('resize', onResize, { passive: true });

    // Initial paint.
    applyProgress(computeTargetProgress());
  }

  function isFirstContentSection(root) {
    var main = root.closest('main') || document.querySelector('main');
    if (!main) return false;
    // Walk up from `root` to find the top-level section wrapper that is a
    // direct concern of `main`, then check it's the first one rendered.
    var node = root;
    while (node.parentElement && node.parentElement !== main) {
      node = node.parentElement;
    }
    var firstElementInMain = main.firstElementChild;
    return node === firstElementInMain;
  }

  function initTransparentHeader(root, pin) {
    if (root.dataset.transparentHeader !== 'true') return;
    if (!isFirstContentSection(root)) return;

    var header = document.querySelector('.header-wrapper') || document.querySelector('header.header');
    if (!header) return;

    var headerHeight = header.getBoundingClientRect().height;
    if (headerHeight > 0) {
      document.documentElement.style.setProperty('--hero-header-overlay-height', headerHeight + 'px');
      root.classList.add('hero-scroll-cta--header-overlay');
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          header.classList.toggle('hero-scroll-cta-header--transparent', entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    observer.observe(pin);

    window.addEventListener(
      'resize',
      function () {
        var h = header.getBoundingClientRect().height;
        if (h > 0) {
          document.documentElement.style.setProperty('--hero-header-overlay-height', h + 'px');
        }
      },
      { passive: true }
    );
  }

  function init() {
    var instances = document.querySelectorAll(SELECTOR);
    instances.forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a section instance is added/edited via the Theme Editor.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector(SELECTOR);
    if (root) initInstance(root);
  });
})();
